/**
 * AI Recruit - Organization Settings Page
 * Accessible from the user-icon dropdown (admin, super_admin, manager).
 *
 * Layout: fixed left navigation panel + scrollable content panel.
 * Navigation items are defined in NAV_ITEMS; each renders its own content component.
 */

import React, { useState, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import toast from 'react-hot-toast'
import {
  UserGroupIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  UserPlusIcon,
  UserMinusIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline'
import { ShieldCheckIcon } from '@heroicons/react/24/solid'

import UsersPage from '@/pages/settings/UsersPage'
import SourceManagementPage from '@/pages/settings/SourceManagementPage'
import ResumeFormatterTemplatesPage from '@/pages/settings/ResumeFormatterTemplatesPage'
import QuestionTypeManagementPage from '@/pages/settings/QuestionTypeManagementPage'
import ComplexityLevelManagementPage from '@/pages/settings/ComplexityLevelManagementPage'
import ReportsPage from '@/pages/settings/ReportsPage'
import AssessmentSupportPage from '@/pages/settings/AssessmentSupportPage'
import CostDashboardPage from '@/pages/settings/CostDashboardPage'
import RolesPermissionsPage from '@/pages/settings/RolesPermissionsPage'
import { OrgSettingsLayout, useOrgSettingsNavItems, type SectionKey } from '@/pages/settings/OrgSettingsLayout'

import type { RootState } from '@/stores'
import { teamsApi, usersApi } from '@/services/api'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import { usePermission } from '@/hooks/usePermission'

// ─── Types ──────────────────────────────────────────────────────────────────

interface TeamMember {
  id: string
  full_name: string
  email: string
  role: string
}

interface Team {
  id: string
  name: string
  organization_id: string
  manager_id: string
  manager_name: string
  manager_email: string
  member_ids: string[]
  members: TeamMember[]
  created_by_id: string
  created_at: string
  updated_at: string
}

interface OrgUser {
  id: string
  full_name: string
  email: string
  role: string
}

// ─── Shared helpers ──────────────────────────────────────────────────────────

const RoleBadge: React.FC<{ role: string }> = ({ role }) => {
  const map: Record<string, string> = {
    super_admin: 'bg-purple-100 text-purple-700',
    admin: 'bg-red-100 text-red-700',
    manager: 'bg-blue-100 text-blue-700',
    senior_recruiter: 'bg-indigo-100 text-indigo-700',
    recruiter: 'bg-green-100 text-green-700',
    viewer: 'bg-gray-100 text-gray-600',
  }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${map[role] ?? 'bg-gray-100 text-gray-600'}`}>
      {role.replace('_', ' ')}
    </span>
  )
}

const Avatar: React.FC<{ name: string; size?: 'sm' | 'md' }> = ({ name, size = 'sm' }) => {
  const initials = name.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase()
  const palette = ['bg-violet-500', 'bg-blue-500', 'bg-teal-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500']
  const bg = palette[initials.charCodeAt(0) % palette.length]
  const sz = size === 'sm' ? 'w-7 h-7 text-xs' : 'w-9 h-9 text-sm'
  return (
    <div className={`${sz} ${bg} rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0`}>
      {initials}
    </div>
  )
}

// ─── Confirm Delete Modal ────────────────────────────────────────────────────

const ConfirmDeleteModal: React.FC<{
  teamName: string
  loading: boolean
  onConfirm: () => void
  onCancel: () => void
}> = ({ teamName, loading, onConfirm, onCancel }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
    <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 mx-4">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
          <TrashIcon className="w-5 h-5 text-red-600" />
        </div>
        <h3 className="text-base font-semibold text-gray-900">Delete Team</h3>
      </div>
      <p className="text-sm text-gray-600 mb-6">
        Are you sure you want to delete <span className="font-medium text-gray-900">"{teamName}"</span>? This cannot be undone.
      </p>
      <div className="flex gap-3 justify-end">
        <button onClick={onCancel} className="btn-outline text-sm px-4 py-2 rounded-lg">Cancel</button>
        <button
          onClick={onConfirm}
          disabled={loading}
          className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
        >
          {loading ? 'Deleting…' : 'Delete'}
        </button>
      </div>
    </div>
  </div>
)

// ─── Team Form Modal (Create / Edit) ────────────────────────────────────────

const TeamFormModal: React.FC<{
  mode: 'create' | 'edit'
  initialName?: string
  initialManagerId?: string
  orgUsers: OrgUser[]
  loading: boolean
  onSubmit: (name: string, managerId: string) => void
  onClose: () => void
}> = ({ mode, initialName = '', initialManagerId = '', orgUsers, loading, onSubmit, onClose }) => {
  const [name, setName] = useState(initialName)
  const [managerId, setManagerId] = useState(initialManagerId)
  const [search, setSearch] = useState('')
  const [errors, setErrors] = useState<{ name?: string; manager?: string }>({})

  const eligible = useMemo(
    () => orgUsers.filter(
      (u) =>
        u.role === 'manager' &&
        (u.full_name.toLowerCase().includes(search.toLowerCase()) ||
          u.email.toLowerCase().includes(search.toLowerCase()))
    ),
    [orgUsers, search]
  )

  const selected = orgUsers.find((u) => u.id === managerId)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const newErrors: { name?: string; manager?: string } = {}
    if (!name.trim()) newErrors.name = 'Team name is required'
    else if (name.trim().length > 50) newErrors.name = 'Team name must be 50 characters or less'
    if (!managerId) newErrors.manager = 'Please select a manager'
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return }
    onSubmit(name.trim(), managerId)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">
            {mode === 'create' ? 'Create New Team' : 'Edit Team'}
          </h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 text-gray-500">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="px-6 py-4 space-y-5 overflow-y-auto flex-1">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Team Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => { setName(e.target.value); if (errors.name) setErrors((prev) => ({ ...prev, name: undefined })) }}
                placeholder="e.g. Engineering East, Sales Team A…"
                maxLength={50}
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Assign Manager <span className="text-red-500">*</span>
              </label>
              {errors.manager && <p className="mb-1.5 text-xs text-red-500">{errors.manager}</p>}
              {selected && (
                <div className="mb-2 flex items-center gap-2 px-3 py-2 bg-primary-50 border border-primary-200 rounded-lg">
                  <Avatar name={selected.full_name} size="sm" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{selected.full_name}</p>
                    <p className="text-xs text-gray-500 truncate">{selected.email}</p>
                  </div>
                  <RoleBadge role={selected.role} />
                  <button type="button" onClick={() => setManagerId('')} className="ml-1 text-gray-400 hover:text-gray-600">
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              )}
              <div className="relative mb-2">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name or email…"
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="max-h-44 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                {eligible.length === 0 ? (
                  <p className="px-3 py-3 text-sm text-gray-500 text-center">No eligible users found</p>
                ) : (
                  eligible.map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => { setManagerId(u.id); setSearch(''); setErrors((prev) => ({ ...prev, manager: undefined })) }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors ${managerId === u.id ? 'bg-primary-50' : ''}`}
                    >
                      <Avatar name={u.full_name} size="sm" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{u.full_name}</p>
                        <p className="text-xs text-gray-500 truncate">{u.email}</p>
                      </div>
                      <RoleBadge role={u.role} />
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
          <div className="px-6 py-4 border-t border-gray-100 flex gap-3 justify-end">
            <button type="button" onClick={onClose} className="btn-outline text-sm px-4 py-2 rounded-lg">Cancel</button>
            <button type="submit" disabled={loading} className="btn-primary text-sm px-5 py-2 rounded-lg disabled:opacity-50">
              {loading
                ? mode === 'create' ? 'Creating…' : 'Saving…'
                : mode === 'create' ? 'Create Team' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Manage Members Modal ────────────────────────────────────────────────────

const ManageMembersModal: React.FC<{
  team: Team
  orgUsers: OrgUser[]
  canEdit: boolean
  mutating: boolean
  onAddMembers: (userIds: string[]) => void
  onRemoveMembers: (userIds: string[]) => void
  onClose: () => void
}> = ({ team, orgUsers, canEdit, mutating, onAddMembers, onRemoveMembers, onClose }) => {
  const [search, setSearch] = useState('')
  const [addSearch, setAddSearch] = useState('')
  const [selectedToAdd, setSelectedToAdd] = useState<Set<string>>(new Set())
  const [selectedToRemove, setSelectedToRemove] = useState<Set<string>>(new Set())

  // Memoize memberIds so addableUsers recomputes whenever team.member_ids changes
  const memberIds = useMemo(() => new Set(team.member_ids), [team.member_ids])

  const filteredMembers = useMemo(
    () => team.members.filter(
      (m) =>
        m.full_name.toLowerCase().includes(search.toLowerCase()) ||
        m.email.toLowerCase().includes(search.toLowerCase())
    ),
    [team.members, search]
  )

  const addableUsers = useMemo(
    () => orgUsers.filter(
      (u) =>
        !memberIds.has(u.id) &&
        u.id !== team.manager_id &&
        (u.full_name.toLowerCase().includes(addSearch.toLowerCase()) ||
          u.email.toLowerCase().includes(addSearch.toLowerCase()))
    ),
    [orgUsers, memberIds, team.manager_id, addSearch]
  )

  const allAddVisible = addableUsers.length > 0 && addableUsers.every((u) => selectedToAdd.has(u.id))
  const allRemoveVisible = filteredMembers.length > 0 && filteredMembers.every((m) => selectedToRemove.has(m.id))

  const toggleAdd = (id: string) => setSelectedToAdd((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
  const toggleRemove = (id: string) => setSelectedToRemove((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })

  const toggleSelectAllAdd = () => {
    if (allAddVisible) {
      setSelectedToAdd((prev) => { const n = new Set(prev); addableUsers.forEach((u) => n.delete(u.id)); return n })
    } else {
      setSelectedToAdd((prev) => { const n = new Set(prev); addableUsers.forEach((u) => n.add(u.id)); return n })
    }
  }

  const toggleSelectAllRemove = () => {
    if (allRemoveVisible) {
      setSelectedToRemove((prev) => { const n = new Set(prev); filteredMembers.forEach((m) => n.delete(m.id)); return n })
    } else {
      setSelectedToRemove((prev) => { const n = new Set(prev); filteredMembers.forEach((m) => n.add(m.id)); return n })
    }
  }

  const handleAddSelected = () => {
    onAddMembers([...selectedToAdd])
    setSelectedToAdd(new Set())
  }

  const handleRemoveSelected = () => {
    onRemoveMembers([...selectedToRemove])
    setSelectedToRemove(new Set())
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl min-w-[540px] mx-4 flex flex-col max-h-[88vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-semibold text-gray-900">{team.name}</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Manager: <span className="font-medium text-gray-700">{team.manager_name}</span>
              {' · '}{team.members.length} {team.members.length === 1 ? 'member' : 'members'}
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 text-gray-500">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="flex flex-1 overflow-hidden divide-x divide-gray-100">
          {/* Current members */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="px-4 pt-4 pb-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Current Members</p>
                {canEdit && filteredMembers.length > 0 && (
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={allRemoveVisible}
                      onChange={toggleSelectAllRemove}
                      className="w-3.5 h-3.5 rounded border-gray-300 text-red-600 focus:ring-red-500"
                    />
                    <span className="text-xs text-gray-500">Select all</span>
                  </label>
                )}
              </div>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search members…"
                  className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 space-y-1">
              {filteredMembers.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-28 text-center">
                  <UserGroupIcon className="w-7 h-7 text-gray-300 mb-2" />
                  <p className="text-sm text-gray-400">No members yet</p>
                </div>
              ) : (
                filteredMembers.map((m) => (
                  <div
                    key={m.id}
                    onClick={() => canEdit && toggleRemove(m.id)}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                      canEdit ? 'cursor-pointer' : ''
                    } ${
                      selectedToRemove.has(m.id) ? 'bg-red-50 border border-red-200' : 'hover:bg-gray-50 border border-transparent'
                    }`}
                  >
                    {canEdit && (
                      <input
                        type="checkbox"
                        checked={selectedToRemove.has(m.id)}
                        onChange={() => toggleRemove(m.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500 flex-shrink-0"
                      />
                    )}
                    <Avatar name={m.full_name} size="sm" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate" title={m.full_name}>{m.full_name}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <RoleBadge role={m.role} />
                        <p className="text-xs text-gray-500 truncate" title={m.email}>{m.email}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            {/* Remove selected button */}
            {canEdit && (
              <div className="px-4 py-3 border-t border-gray-100 mt-1">
                <button
                  onClick={handleRemoveSelected}
                  disabled={selectedToRemove.size === 0 || mutating}
                  className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-red-600 text-white hover:bg-red-700 disabled:bg-red-600"
                >
                  <UserMinusIcon className="w-4 h-4" />
                  {selectedToRemove.size > 0 ? `Remove ${selectedToRemove.size} Selected` : 'Select members to remove'}
                </button>
              </div>
            )}
          </div>
          {/* Add members */}
          {canEdit && (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="px-4 pt-4 pb-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Add Members</p>
                  {addableUsers.length > 0 && (
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={allAddVisible}
                        onChange={toggleSelectAllAdd}
                        className="w-3.5 h-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-xs text-gray-500">Select all</span>
                    </label>
                  )}
                </div>
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={addSearch}
                    onChange={(e) => setAddSearch(e.target.value)}
                    placeholder="Search users…"
                    className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto px-4 space-y-1">
                {addableUsers.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-28 text-center">
                    <p className="text-sm text-gray-400">
                      {addSearch ? 'No users match' : 'All users already added'}
                    </p>
                  </div>
                ) : (
                  addableUsers.map((u) => (
                    <div
                      key={u.id}
                      onClick={() => toggleAdd(u.id)}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                        selectedToAdd.has(u.id) ? 'bg-primary-50 border border-primary-200' : 'hover:bg-gray-50 border border-transparent'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedToAdd.has(u.id)}
                        onChange={() => toggleAdd(u.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 flex-shrink-0"
                      />
                      <Avatar name={u.full_name} size="sm" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate" title={u.full_name}>{u.full_name}</p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <RoleBadge role={u.role} />
                          <p className="text-xs text-gray-500 truncate" title={u.email}>{u.email}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              {/* Add selected button */}
              <div className="px-4 py-3 border-t border-gray-100 mt-1">
                <button
                  onClick={handleAddSelected}
                  disabled={selectedToAdd.size === 0 || mutating}
                  className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-primary-600 text-white hover:bg-primary-700 disabled:bg-primary-600"
                >
                  <UserPlusIcon className="w-4 h-4" />
                  {selectedToAdd.size > 0 ? `Add ${selectedToAdd.size} Selected` : 'Select users to add'}
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="px-6 py-3 border-t border-gray-100 flex justify-end">
          <button onClick={onClose} className="btn-outline text-sm px-4 py-2 rounded-lg">Close</button>
        </div>
      </div>
    </div>
  )
}

// ─── Team Management Section ─────────────────────────────────────────────────

const TeamManagement: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth)
  const qc = useQueryClient()

  // Rewritten 2026-08-14 (§10.1) onto the real backend capability split —
  // same reasoning and shape as TeamConfigurationPage.tsx's equivalent.
  // `canManageOwnTeam` gates the single "Manage members" button (one modal,
  // no separate add/remove gating) — split 2026-08-23 from the old shared
  // `teams.manageOwn` into `teams.addMemberOwn`/`removeMemberOwn`, so this
  // stays an OR of both, preserving exactly who can open that modal today.
  // `canManageAllTeams` gates Create/Edit/Delete team + the stats grid, all
  // as one flag — split 2026-08-23 from the old shared `teams.manageAll`
  // into teams.create/rename/reassignManager/delete, kept combined here
  // for the same reason as TeamConfigurationPage.tsx's equivalent (no
  // pre-existing per-button distinction, every current role holds all
  // four together).
  const { can, bypassesOwnership } = usePermission()
  const canManageAllTeams =
    can('teams.create') || can('teams.rename') || can('teams.reassignManager') || can('teams.delete')
  const canManageOwnTeam = can('teams.addMemberOwn') || can('teams.removeMemberOwn')
  // Defense-in-depth for the default-tab fix above — even if this component
  // is ever reached without the Teams permission, the forbidden request
  // itself never fires.
  const canViewTeams = can('teams.viewOwn') || canManageAllTeams
  // Gate mismatch fix (Priority 4 audit) — the org-users fetch backing the
  // "add member" picker below has always been gated on users.viewRoster on
  // the backend (gated("users.viewRoster", ...), users.py's
  // get_all_recruiters), with no matching frontend check.
  const canViewRoster = can('users.viewRoster')

  const [createOpen, setCreateOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<Team | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Team | null>(null)
  const [membersTarget, setMembersTarget] = useState<Team | null>(null)

  const { data: teamsData, isLoading } = useQuery({
    queryKey: ['teams'],
    queryFn: () => teamsApi.list().then((r) => r.data as { teams: Team[]; total: number }),
    refetchOnMount: 'always',
    enabled: canViewTeams,
  })

  const { data: usersData } = useQuery({
    queryKey: ['org-users'],
    queryFn: () =>
      usersApi.getAllRecruiters().then((r) => {
        const raw = r.data
        return (raw?.users ?? raw?.items ?? raw ?? []).map((u: any) => ({
          id: u.id,
          full_name: u.full_name,
          email: u.email,
          role: u.role,
        })) as OrgUser[]
      }),
    enabled: canViewRoster,
  })

  const orgUsers: OrgUser[] = usersData ?? []
  const teams: Team[] = teamsData?.teams ?? []

  const createMut = useMutation({
    mutationFn: ({ name, managerId }: { name: string; managerId: string }) =>
      teamsApi.create({ name, manager_id: managerId }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['teams'] }); toast.success('Team created'); setCreateOpen(false) },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? 'Failed to create team'),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, name, managerId }: { id: string; name: string; managerId: string }) =>
      teamsApi.update(id, { name, manager_id: managerId }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['teams'] }); toast.success('Team updated'); setEditTarget(null) },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? 'Failed to update team'),
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => teamsApi.delete(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['teams'] }); toast.success('Team deleted'); setDeleteTarget(null) },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? 'Failed to delete team'),
  })

  const addMembersMut = useMutation({
    mutationFn: async ({ teamId, userIds }: { teamId: string; userIds: string[] }) => {
      for (const userId of userIds) {
        await teamsApi.addMember(teamId, userId)
      }
      // Fetch fresh team after all adds so modal reflects DB state
      return teamsApi.get(teamId)
    },
    onSuccess: (res, vars) => {
      qc.invalidateQueries({ queryKey: ['teams'] })
      setMembersTarget(res.data as Team)
      toast.success(`${vars.userIds.length} member${vars.userIds.length > 1 ? 's' : ''} added`)
    },
    // Skips its own toast on a 403 (bug fix, 2026-08-26) — the shared apiV2
    // response interceptor (services/api.ts) already toasts every 403
    // app-wide; this duplicated it, showing the identical "Missing
    // permission: ..." message twice for one denied attempt. Still toasts
    // for any other error type, since the interceptor only acts on 403.
    onError: (e: any) => {
      if (e?.response?.status === 403) return
      toast.error(e?.response?.data?.detail ?? 'Failed to add members')
    },
  })

  const removeMembersMut = useMutation({
    mutationFn: async ({ teamId, userIds }: { teamId: string; userIds: string[] }) => {
      for (const userId of userIds) {
        await teamsApi.removeMember(teamId, userId)
      }
      // Fetch fresh team after all removes so modal reflects DB state
      return teamsApi.get(teamId)
    },
    onSuccess: (res, vars) => {
      qc.invalidateQueries({ queryKey: ['teams'] })
      setMembersTarget(res.data as Team)
      toast.success(`${vars.userIds.length} member${vars.userIds.length > 1 ? 's' : ''} removed`)
    },
    // Same duplicate-toast fix as addMembersMut above.
    onError: (e: any) => {
      if (e?.response?.status === 403) return
      toast.error(e?.response?.data?.detail ?? 'Failed to remove members')
    },
  })

  const totalMembers = teams.reduce((a, t) => a + t.members.length, 0)
  const uniqueManagers = new Set(teams.map((t) => t.manager_id)).size

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><LoadingSpinner size="lg" /></div>
  }

  return (
    <div>
      {/* Section header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheckIcon className="w-6 h-6 text-primary-600" />
            Team Management
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {canManageAllTeams
              ? 'Create teams, assign managers, and manage team membership.'
              : 'Manage your team members.'}
          </p>
        </div>
        {canManageAllTeams && (
          <button
            onClick={() => setCreateOpen(true)}
            className="btn-primary flex items-center gap-2 text-sm px-4 py-2.5 rounded-lg"
          >
            <PlusIcon className="w-4 h-4" />
            Create Team
          </button>
        )}
      </div>

      {/* Stats — admin only */}
      {canManageAllTeams && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[
            { label: 'Total Teams', value: teams.length, cls: 'text-primary-600' },
            { label: 'Total Members', value: totalMembers, cls: 'text-emerald-600' },
            { label: 'Managers', value: uniqueManagers, cls: 'text-violet-600' },
          ].map((s) => (
            <div key={s.label} className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <p className="text-xs text-gray-500 font-medium mb-1">{s.label}</p>
              <p className={`text-2xl font-bold ${s.cls}`}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Teams table */}
      {teams.length === 0 ? (
        <div className="border border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center py-16 text-center bg-gray-50">
          <div className="w-14 h-14 bg-white border border-gray-200 rounded-full flex items-center justify-center mb-4 shadow-sm">
            <UserGroupIcon className="w-7 h-7 text-gray-400" />
          </div>
          <h3 className="text-base font-semibold text-gray-900 mb-1">No teams yet</h3>
          <p className="text-sm text-gray-500 max-w-xs">
            {canManageAllTeams
              ? 'Create your first team to start organizing your recruitment workforce.'
              : 'You have not been assigned as manager to any team yet.'}
          </p>
          {canManageAllTeams && (
            <button
              onClick={() => setCreateOpen(true)}
              className="mt-5 btn-primary flex items-center gap-2 text-sm px-4 py-2.5 rounded-lg"
            >
              <PlusIcon className="w-4 h-4" />
              Create First Team
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead>
              <tr className="bg-gray-50">
                {['Team', 'Manager', 'Members', '#', 'Actions'].map((h) => (
                  <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {teams.map((team) => {
                const canManageMembers = bypassesOwnership || team.manager_id === user?.id
                const displayed = team.members.slice(0, 5)
                const overflow = team.members.length - displayed.length

                return (
                  <tr key={team.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <UserGroupIcon className="w-5 h-5 text-primary-600" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">{team.name}</p>
                          <p className="text-xs text-gray-500 whitespace-nowrap">Created {new Date(team.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <Avatar name={team.manager_name} size="sm" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate max-w-[150px]">{team.manager_name}</p>
                          <p className="text-xs text-gray-500 truncate max-w-[150px]">{team.manager_email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1">
                        {displayed.map((m) => (
                          <div key={m.id} title={m.full_name}><Avatar name={m.full_name} size="sm" /></div>
                        ))}
                        {overflow > 0 && (
                          <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-600">
                            +{overflow}
                          </div>
                        )}
                        {team.members.length === 0 && <span className="text-xs text-gray-400 italic">No members</span>}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-xs font-semibold text-gray-700">
                        {team.members.length}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1">
                        {/* Disabled (not hidden) for a manager viewing a team
                            they don't own — RBAC_UI_IMPLEMENTATION_PLAN.md §5.7,
                            same as TeamConfigurationPage. */}
                        {canManageOwnTeam && (
                          <button
                            onClick={() => canManageMembers && setMembersTarget(team)}
                            disabled={!canManageMembers}
                            title={canManageMembers ? 'Manage members' : "You're not this team's manager"}
                            className={
                              canManageMembers
                                ? 'p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors'
                                : 'p-1.5 rounded-lg text-gray-300 cursor-not-allowed'
                            }
                          >
                            <UserGroupIcon className="w-4 h-4" />
                          </button>
                        )}
                        {canManageAllTeams && (
                          <>
                            <button
                              onClick={() => setEditTarget(team)}
                              title="Edit team"
                              className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                            >
                              <PencilSquareIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setDeleteTarget(team)}
                              title="Delete team"
                              className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {createOpen && (
        <TeamFormModal
          mode="create"
          orgUsers={orgUsers}
          loading={createMut.isPending}
          onSubmit={(name, managerId) => createMut.mutate({ name, managerId })}
          onClose={() => setCreateOpen(false)}
        />
      )}
      {editTarget && (
        <TeamFormModal
          mode="edit"
          initialName={editTarget.name}
          initialManagerId={editTarget.manager_id}
          orgUsers={orgUsers}
          loading={updateMut.isPending}
          onSubmit={(name, managerId) => updateMut.mutate({ id: editTarget.id, name, managerId })}
          onClose={() => setEditTarget(null)}
        />
      )}
      {deleteTarget && (
        <ConfirmDeleteModal
          teamName={deleteTarget.name}
          loading={deleteMut.isPending}
          onConfirm={() => deleteMut.mutate(deleteTarget.id)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
      {membersTarget && (
        <ManageMembersModal
          team={membersTarget}
          orgUsers={orgUsers}
          canEdit={bypassesOwnership || membersTarget.manager_id === user?.id}
          mutating={addMembersMut.isPending || removeMembersMut.isPending}
          onAddMembers={(userIds) => addMembersMut.mutate({ teamId: membersTarget.id, userIds })}
          onRemoveMembers={(userIds) => removeMembersMut.mutate({ teamId: membersTarget.id, userIds })}
          onClose={() => setMembersTarget(null)}
        />
      )}
    </div>
  )
}

// ─── Main Page ───────────────────────────────────────────────────────────────

const OrganizationSettingsPage: React.FC = () => {
  // Tab selection lives in the URL (not plain useState) so that navigating
  // away to a detail page (e.g. Edit Role at /admin/roles/:id) and hitting
  // the browser Back button returns to the tab the user was actually on,
  // instead of resetting to the 'teams' default on remount.
  const [searchParams, setSearchParams] = useSearchParams()
  // Default tab fix (2026-10-11): was hardcoded to 'teams' regardless of
  // the signed-in user's permissions, so a user with e.g. only Sources
  // access still landed on Teams and fired its forbidden GET /teams,
  // surfacing a "Missing permission: teams.viewOwn" toast on page load.
  // Default to Teams only when the user can actually see that nav item;
  // otherwise fall back to their first visible section.
  const { can } = usePermission()
  const canViewTeams =
    can('teams.viewOwn') || can('teams.create') || can('teams.rename') ||
    can('teams.reassignManager') || can('teams.delete')
  const { navItems } = useOrgSettingsNavItems()
  const defaultSection: SectionKey = canViewTeams ? 'teams' : (navItems[0]?.key ?? 'teams')
  const activeSection = (searchParams.get('tab') as SectionKey | null) || defaultSection

  return (
    <OrgSettingsLayout
      activeSection={activeSection}
      onSelect={(key) => setSearchParams({ tab: key }, { replace: true })}
    >
      {activeSection === 'teams' && <TeamManagement />}
      {activeSection === 'users' && <UsersPage />}
      {activeSection === 'sources' && <SourceManagementPage />}
      {activeSection === 'cost-dashboard' && <CostDashboardPage />}
      {activeSection === 'roles' && <RolesPermissionsPage />}
      {activeSection === 'questionTypes' && <QuestionTypeManagementPage />}
      {activeSection === 'complexityLevels' && <ComplexityLevelManagementPage />}
      {activeSection === 'reports' && <ReportsPage />}
      {activeSection === 'assessmentSupport' && <AssessmentSupportPage />}
      {activeSection === 'resumeTemplates' && <ResumeFormatterTemplatesPage />}
    </OrgSettingsLayout>
  )
}

export default OrganizationSettingsPage
